import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import { useToast } from "@/shared/hooks/use-toast";

export type ChatSalaStatus = "aberta" | "encerrada";

export type ChatVinculoTipo =
  | "judicial"
  | "administrativo"
  | "extrajudicial"
  | "procedimento"
  | "atendimento_avulso"
  | null;

export type ChatAtaDestino = "nenhum" | "vinculo" | "apenas_sala";

export interface ChatSala {
  id: string;
  titulo: string;
  assunto: string | null;
  status: ChatSalaStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  encerrada_at: string | null;
  processo_id: string | null;
  processo_adm_id: string | null;
  processo_ext_id: string | null;
  procedimento_id: string | null;
  atendimento_avulso_id: string | null;
  client_id: string | null;
  institution_id: string | null;
  ata_destino?: ChatAtaDestino | string | null;
  ata_texto?: string | null;
  ata_gerada_em?: string | null;
  ultima_mensagem?: string | null;
  ultima_mensagem_at?: string | null;
  nao_lidas?: number;
  participantes?: Array<{ id: string; nome: string }>;
  vinculo_label?: string | null;
}

export interface ChatMensagem {
  id: string;
  sala_id: string;
  autor_id: string;
  conteudo: string;
  created_at: string;
  editado_at: string | null;
  deleted_at: string | null;
  autor_nome?: string;
}

export const CHAT_SALAS_KEY = "chat_salas";
export const CHAT_MSG_KEY = "chat_mensagens";
export const CHAT_LEITURAS_SALA_KEY = "chat_leituras_sala";

export interface ChatLeituraSalaRow {
  profile_id: string;
  ultima_leitura: string;
}

/** Escolhe o marcador mais recente entre ISO strings (preserva string do vencedor). */
export function maxIsoTimestamp(a?: string | null, b?: string | null): string | null {
  if (!a && !b) return null;
  if (!a) return b!;
  if (!b) return a;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  return ta >= tb ? a : b;
}

/**
 * Horário do marcador de leitura ao visualizar a sala.
 * Usa o created_at da última mensagem (relógio do servidor) para não deixar mensagens
 * "presas" como não lidas quando o relógio do navegador está atrás do Postgres.
 */
export function calcularUltimaLeituraAoVisualizar(
  ultimaMensagemCreatedAt?: string | null,
  agoraIso?: string,
): string {
  const agora = agoraIso ?? new Date().toISOString();
  return maxIsoTimestamp(ultimaMensagemCreatedAt, agora) ?? agora;
}

/** Marcador persistido em `chat_leituras` (+1 ms) para `.gt(created_at, ultima_leitura)` zerar a sala. */
export function ultimaLeituraParaPersistir(
  ultimaMensagemCreatedAt?: string | null,
  agoraIso?: string,
): string {
  const base = calcularUltimaLeituraAoVisualizar(ultimaMensagemCreatedAt, agoraIso);
  const ms = new Date(base).getTime();
  if (Number.isNaN(ms)) return base;
  return new Date(ms + 1).toISOString();
}

/** Participantes da sala (exceto o autor) que já tinham marcador de leitura >= horário da mensagem. */
export function participantesLeituraDaMensagem(
  messageCreatedAt: string,
  leituras: ChatLeituraSalaRow[],
  participantes: Array<{ id: string; nome: string }>,
  autorMensagemId: string,
): { lidos: Array<{ id: string; nome: string }>; pendentes: Array<{ id: string; nome: string }> } {
  const tMsg = new Date(messageCreatedAt).getTime();
  const leituraMap = new Map(
    leituras.map((l) => [l.profile_id, new Date(l.ultima_leitura).getTime()] as const),
  );
  const outros = participantes.filter((p) => p.id !== autorMensagemId);
  const lidos: Array<{ id: string; nome: string }> = [];
  const pendentes: Array<{ id: string; nome: string }> = [];
  for (const p of outros) {
    const t = leituraMap.get(p.id);
    if (t != null && !Number.isNaN(t) && t >= tMsg) lidos.push(p);
    else pendentes.push(p);
  }
  return { lidos, pendentes };
}

/** Som de notificação para nova mensagem de chat.
 * Timbre descendente (1200→900 Hz) — diferencia do Correio (ascendente 880→1100 Hz). */
function playChatNotificationSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1200, ctx.currentTime);
    osc.frequency.setValueAtTime(900, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch {
    // AudioContext bloqueado pelo browser — falha silenciosa
  }
}

export function inferirTipoVinculoSala(s: Pick<
  ChatSala,
  "processo_id" | "processo_adm_id" | "processo_ext_id" | "procedimento_id" | "atendimento_avulso_id"
>): ChatVinculoTipo {
  if (s.processo_id) return "judicial";
  if (s.processo_adm_id) return "administrativo";
  if (s.processo_ext_id) return "extrajudicial";
  if (s.procedimento_id) return "procedimento";
  if (s.atendimento_avulso_id) return "atendimento_avulso";
  return null;
}

export function useChatSalas(options?: { refetchIntervalMs?: number | false }) {
  const { user } = useAuth();
  const interval = options?.refetchIntervalMs ?? 12_000;

  return useQuery({
    queryKey: [CHAT_SALAS_KEY, user?.id],
    staleTime: 30_000,
    refetchInterval: interval === false ? false : interval,
    enabled: !!user,
    queryFn: async (): Promise<ChatSala[]> => {
      const { data: myProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (!myProfile?.id) return [];

      const { data: participacao } = await supabase
        .from("chat_participantes")
        .select("sala_id")
        .eq("profile_id", myProfile.id);
      if (!participacao?.length) return [];

      const salaIds = [...new Set(participacao.map((p) => p.sala_id))];

      const { data: salas, error } = await supabase
        .from("chat_salas")
        .select(
          "id, titulo, assunto, status, created_by, created_at, updated_at, encerrada_at, processo_id, processo_adm_id, processo_ext_id, procedimento_id, atendimento_avulso_id, client_id, institution_id, ata_destino, ata_texto, ata_gerada_em",
        )
        .in("id", salaIds)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      if (!salas?.length) return [];

      const ultLimit = Math.min(800, Math.max(120, salaIds.length * 12));
      const ultRes = await supabase
        .from("chat_mensagens")
        .select("sala_id, conteudo, created_at")
        .in("sala_id", salaIds)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(ultLimit);
      const ultimas = ultRes.error ? [] : (ultRes.data ?? []);

      const ultimaMap = new Map<string, { conteudo: string; created_at: string }>();
      for (const m of ultimas) {
        if (!ultimaMap.has(m.sala_id)) ultimaMap.set(m.sala_id, m);
      }

      const leitRes = await supabase
        .from("chat_leituras")
        .select("sala_id, ultima_leitura")
        .eq("profile_id", myProfile.id)
        .in("sala_id", salaIds);
      const leituras = leitRes.error ? [] : (leitRes.data ?? []);
      const leituraMap = new Map(leituras.map((l) => [l.sala_id, l.ultima_leitura]));

      const naoLidasPromises = salaIds.map((sid) =>
        supabase
          .from("chat_mensagens")
          .select("id", { count: "planned", head: true })
          .eq("sala_id", sid)
          .is("deleted_at", null)
          .neq("autor_id", myProfile.id)
          .gt("created_at", leituraMap.get(sid) ?? "1970-01-01T00:00:00.000Z")
          .then((r) => ({ sid, count: r.error ? 0 : r.count ?? 0 }))
          .catch(() => ({ sid, count: 0 })),
      );
      const naoLidasResults = await Promise.all(naoLidasPromises);
      const naoLidasMap = new Map(naoLidasResults.map((r) => [r.sid, r.count]));

      const partsRes = await supabase
        .from("chat_participantes")
        .select("sala_id, profile_id")
        .in("sala_id", salaIds);
      const parts = partsRes.error ? [] : (partsRes.data ?? []);
      const profIds = [...new Set(parts.map((p) => p.profile_id))];
      let profs: { id: string | null; nome: string | null }[] = [];
      if (profIds.length) {
        const pr = await supabase.from("profiles_public").select("id, nome").in("id", profIds);
        profs = pr.error ? [] : (pr.data ?? []);
      }
      const profMap = new Map(profs.map((p) => [p.id, p.nome]));
      const partsMap = new Map<string, Array<{ id: string; nome: string }>>();
      for (const p of parts) {
        const list = partsMap.get(p.sala_id) ?? [];
        list.push({ id: p.profile_id, nome: profMap.get(p.profile_id) ?? "?" });
        partsMap.set(p.sala_id, list);
      }

      return salas.map((s) => ({
        ...s,
        status: s.status as ChatSalaStatus,
        ultima_mensagem: ultimaMap.get(s.id)?.conteudo ?? null,
        ultima_mensagem_at: ultimaMap.get(s.id)?.created_at ?? null,
        nao_lidas: naoLidasMap.get(s.id) ?? 0,
        participantes: partsMap.get(s.id) ?? [],
      }));
    },
  });
}

export function useChatMensagens(salaId: string | null) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CHAT_MSG_KEY, salaId],
    staleTime: 10_000,
    enabled: !!salaId,
    queryFn: async (): Promise<ChatMensagem[]> => {
      const { data: rows, error } = await supabase
        .from("chat_mensagens")
        .select("id, sala_id, autor_id, conteudo, created_at, editado_at, deleted_at")
        .eq("sala_id", salaId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: true })
        .limit(200);
      if (error) throw error;
      if (!rows?.length) return [];

      const autorIds = [...new Set(rows.map((r) => r.autor_id))];
      const pr = await supabase.from("profiles_public").select("id, nome").in("id", autorIds);
      const profs = pr.error ? [] : (pr.data ?? []);
      const profMap = new Map(profs.map((p) => [p.id, p.nome]));

      return rows.map((r) => ({ ...r, autor_nome: profMap.get(r.autor_id) ?? "?" }));
    },
  });

  useEffect(() => {
    if (!salaId) return;
    const channel = supabase
      .channel(`chat-msgs-${salaId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_mensagens", filter: `sala_id=eq.${salaId}` },
        () => {
          qc.invalidateQueries({ queryKey: [CHAT_MSG_KEY, salaId] });
          qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
          playChatNotificationSound();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [salaId, qc]);

  return query;
}

export function useChatLeiturasSala(salaId: string | null) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CHAT_LEITURAS_SALA_KEY, salaId],
    staleTime: 5_000,
    enabled: !!salaId,
    queryFn: async (): Promise<ChatLeituraSalaRow[]> => {
      const { data, error } = await supabase
        .from("chat_leituras")
        .select("profile_id, ultima_leitura")
        .eq("sala_id", salaId!);
      if (error) throw error;
      return (data ?? []) as ChatLeituraSalaRow[];
    },
  });

  useEffect(() => {
    if (!salaId) return;
    const channel = supabase
      .channel(`chat-leituras-${salaId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chat_leituras",
          filter: `sala_id=eq.${salaId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: [CHAT_LEITURAS_SALA_KEY, salaId] });
          qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [salaId, qc]);

  return query;
}

export function useChatMutations() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const criarSala = useMutation({
    mutationFn: async (payload: {
      titulo: string;
      assunto?: string;
      participanteIds: string[];
      ata_destino?: ChatAtaDestino;
      processo_id?: string | null;
      processo_adm_id?: string | null;
      processo_ext_id?: string | null;
      procedimento_id?: string | null;
      atendimento_avulso_id?: string | null;
      client_id?: string | null;
      institution_id?: string | null;
    }) => {
      const { data: myProfile } = await supabase.from("profiles").select("id").eq("user_id", user!.id).maybeSingle();
      if (!myProfile?.id) throw new Error("Perfil não encontrado");

      const ataDestino = payload.ata_destino ?? "nenhum";

      const { data: sala, error: sErr } = await supabase
        .from("chat_salas")
        .insert({
          titulo: payload.titulo,
          assunto: payload.assunto ?? null,
          created_by: myProfile.id,
          ata_destino: ataDestino,
          processo_id: payload.processo_id ?? null,
          processo_adm_id: payload.processo_adm_id ?? null,
          processo_ext_id: payload.processo_ext_id ?? null,
          procedimento_id: payload.procedimento_id ?? null,
          atendimento_avulso_id: payload.atendimento_avulso_id ?? null,
          client_id: payload.client_id ?? null,
          institution_id: payload.institution_id ?? null,
        })
        .select("id")
        .single();
      if (sErr) throw sErr;

      const todosIds = [...new Set([myProfile.id, ...payload.participanteIds])];
      const { error: pErr } = await supabase
        .from("chat_participantes")
        .insert(todosIds.map((pid) => ({ sala_id: sala.id, profile_id: pid })));
      if (pErr) {
        await supabase.from("chat_salas").delete().eq("id", sala.id);
        throw pErr;
      }

      return sala.id as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
      toast({ title: "Sala criada com sucesso." });
    },
    onError: (e: Error) =>
      toast({ title: "Erro ao criar sala", description: e.message, variant: "destructive" }),
  });

  const enviarMensagem = useMutation({
    mutationFn: async ({ salaId, conteudo }: { salaId: string; conteudo: string }) => {
      const { data: myProfile } = await supabase.from("profiles").select("id").eq("user_id", user!.id).maybeSingle();
      if (!myProfile?.id) throw new Error("Perfil não encontrado");

      const { error } = await supabase.from("chat_mensagens").insert({
        sala_id: salaId,
        autor_id: myProfile.id,
        conteudo: conteudo.trim(),
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [CHAT_MSG_KEY, vars.salaId] });
      qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
    },
    onError: (e: Error) =>
      toast({ title: "Erro ao enviar", description: e.message, variant: "destructive" }),
  });

  const encerrarSala = useMutation({
    mutationFn: async (vars: { salaId: string; ataDestino?: string | null }) => {
      const { salaId } = vars;
      const { error } = await supabase
        .from("chat_salas")
        .update({ status: "encerrada", encerrada_at: new Date().toISOString() })
        .eq("id", salaId);
      if (error) throw error;
      return { salaId, ataDestino: vars.ataDestino ?? "nenhum" };
    },
    onSuccess: async (ret) => {
      qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
      toast({ title: "Sala encerrada." });
      if (ret.ataDestino && ret.ataDestino !== "nenhum") {
        const { data, error } = await supabase.functions.invoke("chat-operacional-ata", {
          body: { salaId: ret.salaId },
        });
        if (error) {
          toast({
            title: "Ata automática",
            description: error.message || "Não foi possível gerar a ata agora. Tente de novo em instantes.",
            variant: "destructive",
          });
        } else {
          const d = data as { ok?: boolean; skipped?: boolean; registrado_vinculo?: boolean } | null;
          if (d?.ok && !d?.skipped) {
            toast({
              title: "Ata gerada",
              description: d.registrado_vinculo
                ? "Resumo salvo na sala e registrado no vínculo."
                : "Resumo salvo na sala.",
            });
          }
        }
        qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
      }
    },
  });

  const deletarSala = useMutation({
    mutationFn: async (salaId: string) => {
      const { error } = await supabase.from("chat_salas").delete().eq("id", salaId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY] });
      toast({ title: "Sala removida." });
    },
  });

  const marcarLido = useMutation({
    mutationFn: async (vars: { salaId: string; ate?: string | null }) => {
      const { salaId, ate } = vars;
      const { data: myProfile } = await supabase.from("profiles").select("id").eq("user_id", user!.id).maybeSingle();
      if (!myProfile?.id) throw new Error("Perfil não encontrado");

      const candidato = ultimaLeituraParaPersistir(ate);

      const existenteRes = await supabase
        .from("chat_leituras")
        .select("ultima_leitura")
        .eq("sala_id", salaId)
        .eq("profile_id", myProfile.id)
        .maybeSingle();
      const ultima_leitura =
        maxIsoTimestamp(existenteRes.data?.ultima_leitura, candidato) ?? candidato;

      const { error } = await supabase.from("chat_leituras").upsert(
        { sala_id: salaId, profile_id: myProfile.id, ultima_leitura },
        { onConflict: "sala_id,profile_id" },
      );
      if (error) throw error;
    },
    onMutate: async ({ salaId }) => {
      await qc.cancelQueries({ queryKey: [CHAT_SALAS_KEY, user?.id] });
      const previous = qc.getQueryData<ChatSala[]>([CHAT_SALAS_KEY, user?.id]);
      qc.setQueryData<ChatSala[] | undefined>([CHAT_SALAS_KEY, user?.id], (old) =>
        old?.map((s) => (s.id === salaId ? { ...s, nao_lidas: 0 } : s)),
      );
      return { previous };
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData([CHAT_SALAS_KEY, user?.id], ctx.previous);
      toast({ title: "Não foi possível marcar como lida", description: e.message, variant: "destructive" });
    },
    onSuccess: (_, { salaId }) => {
      qc.setQueryData<ChatSala[] | undefined>([CHAT_SALAS_KEY, user?.id], (old) =>
        old?.map((s) => (s.id === salaId ? { ...s, nao_lidas: 0 } : s)),
      );
      qc.invalidateQueries({ queryKey: [CHAT_LEITURAS_SALA_KEY, salaId] });
    },
    onSettled: () => {
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY, user?.id] });
      }, 900);
    },
  });

  return { criarSala, enviarMensagem, encerrarSala, deletarSala, marcarLido };
}
